// Per-module search + filter + sort definitions.
// Drives the unified ResultsPageLayout for all 12 services.

import { Plane, Hotel, Train, Bus, Car, Home, Package, MapPin, FileText, Ship, Banknote, ShieldCheck } from 'lucide-react';

const today = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

// Time-of-day buckets for departure/arrival filters
export const TIME_BUCKETS = [
  { id: 'early', label: 'Before 6 AM', from: 0, to: 6 },
  { id: 'morning', label: '6 AM – 12 PM', from: 6, to: 12 },
  { id: 'afternoon', label: '12 PM – 6 PM', from: 12, to: 18 },
  { id: 'evening', label: 'After 6 PM', from: 18, to: 24 },
];

const parseHour = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return 0;
  const m = hhmm.match(/(\d{1,2}):(\d{2})/);
  return m ? parseInt(m[1], 10) : 0;
};

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

export const SEARCH_MODULES = {
  flights: {
    label: 'Flights',
    icon: Plane,
    endpoint: '/v2/flights/search',
    method: 'POST',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'origin', label: 'From', type: 'text', defaultValue: 'DEL', placeholder: 'DEL', uppercase: true, maxLength: 3 },
      { name: 'destination', label: 'To', type: 'text', defaultValue: 'BOM', placeholder: 'BOM', uppercase: true, maxLength: 3 },
      { name: 'departure_date', label: 'Departure', type: 'date', defaultValue: () => today(7) },
      { name: 'adults', label: 'Travellers', type: 'number', defaultValue: 1, min: 1, max: 9 },
    ],
    buildPayload: (s) => ({
      origin: (s.origin || 'DEL').toUpperCase(),
      destination: (s.destination || 'BOM').toUpperCase(),
      departure_date: s.departure_date || today(7),
      adults: parseInt(s.adults, 10) || 1,
      tier: 'b2c',
    }),
    extractResults: (data) => data.flights || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'price_desc', label: 'Price (High → Low)' },
      { value: 'departure_asc', label: 'Earliest Departure' },
      { value: 'duration_asc', label: 'Shortest Duration' },
    ],
    sortFns: {
      price_asc: (a, b) => a.display_price - b.display_price,
      price_desc: (a, b) => b.display_price - a.display_price,
      departure_asc: (a, b) => parseHour(a.departure) - parseHour(b.departure),
      duration_asc: (a, b) => (a.duration || '').localeCompare(b.duration || ''),
    },
    filters: [
      { name: 'stops', label: 'Stops', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.stops === 0 ? 'Non-stop' : `${r.stops} stop`)), apply: (r, v) => v.length === 0 || v.includes(r.stops === 0 ? 'Non-stop' : `${r.stops} stop`) },
      { name: 'airline', label: 'Airlines', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.airline)), apply: (r, v) => v.length === 0 || v.includes(r.airline) },
      { name: 'departureWindow', label: 'Departure Time', type: 'time-bucket', apply: (r, v) => v.length === 0 || v.some((id) => { const b = TIME_BUCKETS.find((x) => x.id === id); const h = parseHour(r.departure); return b && h >= b.from && h < b.to; }) },
      { name: 'refundable', label: 'Refundable only', type: 'toggle', apply: (r, v) => !v || r.is_refundable },
      { name: 'priceRange', label: 'Price', type: 'range', key: 'display_price', apply: (r, [lo, hi]) => (lo == null || r.display_price >= lo) && (hi == null || r.display_price <= hi) },
    ],
  },

  hotels: {
    label: 'Hotels',
    icon: Hotel,
    endpoint: '/v2/hotels/search',
    method: 'POST',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'city', label: 'City', type: 'text', defaultValue: '', placeholder: 'City, Area or Property name' },
      { name: 'lat', label: 'Lat', type: 'hidden', defaultValue: '' },
      { name: 'lng', label: 'Lng', type: 'hidden', defaultValue: '' },
      { name: 'check_in', label: 'Check-in', type: 'date', defaultValue: () => today(7) },
      { name: 'check_out', label: 'Check-out', type: 'date', defaultValue: () => today(9) },
      { name: 'guests', label: 'Guests', type: 'number', defaultValue: 2, min: 1, max: 10 },
      { name: 'rooms', label: 'Rooms', type: 'number', defaultValue: 1, min: 1, max: 5 },
      { name: 'children', label: 'Children', type: 'hidden', defaultValue: 0 },
      { name: 'child_ages', label: 'Child ages', type: 'hidden', defaultValue: '' },
    ],
    buildPayload: (s) => {
      const checkIn = s.check_in || today(7);
      const checkOut = s.check_out || today(9);
      const nights = Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)));
      const children = parseInt(s.children, 10) || 0;
      const ages = String(s.child_ages || '').split(',').map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n));
      return {
        city: s.city || 'Mumbai',
        lat: s.lat ? parseFloat(s.lat) : null,
        lng: s.lng ? parseFloat(s.lng) : null,
        check_in: checkIn,
        check_out: checkOut,
        rooms: parseInt(s.rooms, 10) || 1,
        guests: parseInt(s.guests, 10) || 2,
        children,
        child_ages: ages.slice(0, children),
        nights,
        tier: 'b2c',
      };
    },
    extractResults: (data) => data.hotels || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'price_desc', label: 'Price (High → Low)' },
      { value: 'rating_desc', label: 'Top Rated' },
    ],
    sortFns: {
      price_asc: (a, b) => a.display_price - b.display_price,
      price_desc: (a, b) => b.display_price - a.display_price,
      rating_desc: (a, b) => (b.rating || 0) - (a.rating || 0),
    },
    filters: [], // see HOTEL_FILTERS in hotelFilters.js; ResultsPageLayout injects them for hotels
  },

  buses: {
    label: 'Buses',
    icon: Bus,
    endpoint: '/v2/buses/search',
    method: 'POST',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'origin', label: 'From', type: 'text', defaultValue: 'Delhi', placeholder: 'Delhi' },
      { name: 'destination', label: 'To', type: 'text', defaultValue: 'Jaipur', placeholder: 'Jaipur' },
      { name: 'travel_date', label: 'Travel Date', type: 'date', defaultValue: () => today(7) },
      { name: 'passengers', label: 'Passengers', type: 'number', defaultValue: 1, min: 1, max: 10 },
    ],
    buildPayload: (s) => ({
      origin: s.origin || 'Delhi',
      destination: s.destination || 'Jaipur',
      travel_date: s.travel_date || today(7),
      passengers: parseInt(s.passengers, 10) || 1,
      tier: 'b2c',
    }),
    extractResults: (data) => data.buses || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'price_desc', label: 'Price (High → Low)' },
      { value: 'departure_asc', label: 'Earliest Departure' },
      { value: 'rating_desc', label: 'Top Rated' },
    ],
    sortFns: {
      price_asc: (a, b) => a.display_price - b.display_price,
      price_desc: (a, b) => b.display_price - a.display_price,
      departure_asc: (a, b) => parseHour(a.departure) - parseHour(b.departure),
      rating_desc: (a, b) => (b.rating || 0) - (a.rating || 0),
    },
    filters: [
      { name: 'busType', label: 'Bus Type', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.bus_type)), apply: (r, v) => v.length === 0 || v.includes(r.bus_type) },
      { name: 'operator', label: 'Operators', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.operator)), apply: (r, v) => v.length === 0 || v.includes(r.operator) },
      { name: 'amenities', label: 'Amenities', type: 'checkbox', options: (rs) => uniq(rs.flatMap((r) => r.amenities || [])), apply: (r, v) => v.length === 0 || v.every((a) => (r.amenities || []).includes(a)) },
      { name: 'departureWindow', label: 'Departure Time', type: 'time-bucket', apply: (r, v) => v.length === 0 || v.some((id) => { const b = TIME_BUCKETS.find((x) => x.id === id); const h = parseHour(r.departure); return b && h >= b.from && h < b.to; }) },
      { name: 'priceRange', label: 'Price', type: 'range', key: 'display_price', apply: (r, [lo, hi]) => (lo == null || r.display_price >= lo) && (hi == null || r.display_price <= hi) },
    ],
  },

  // Modules below use v1 catalog endpoints (GET, no body) until real provider wired up
  trains: {
    label: 'Trains',
    icon: Train,
    endpoint: '/services/trains',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'origin', label: 'From', type: 'text', defaultValue: 'Delhi', placeholder: 'Delhi' },
      { name: 'destination', label: 'To', type: 'text', defaultValue: 'Mumbai', placeholder: 'Mumbai' },
      { name: 'travel_date', label: 'Travel Date', type: 'date', defaultValue: () => today(7) },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'departure_asc', label: 'Earliest Departure' },
    ],
    sortFns: {
      price_asc: (a, b) => a.price - b.price,
      departure_asc: (a, b) => parseHour(a.departure) - parseHour(b.departure),
    },
    filters: [
      { name: 'class', label: 'Class', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.class)), apply: (r, v) => v.length === 0 || v.includes(r.class) },
      { name: 'type', label: 'Type', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.type)), apply: (r, v) => v.length === 0 || v.includes(r.type) },
      { name: 'priceRange', label: 'Price', type: 'range', key: 'price', apply: (r, [lo, hi]) => (lo == null || r.price >= lo) && (hi == null || r.price <= hi) },
    ],
  },

  cabs: {
    label: 'Cabs',
    icon: Car,
    endpoint: '/services/cabs',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'pickup', label: 'Pickup', type: 'text', defaultValue: '', placeholder: 'Pickup location' },
      { name: 'drop', label: 'Drop', type: 'text', defaultValue: '', placeholder: 'Drop location' },
      { name: 'pickup_date', label: 'Pickup Date', type: 'date', defaultValue: () => today(1) },
      { name: 'trip_type', label: 'Trip Type', type: 'select', options: ['Local', 'Outstation', 'Airport'], defaultValue: 'Local' },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'rating_desc', label: 'Top Rated' },
    ],
    sortFns: {
      price_asc: (a, b) => a.price - b.price,
      rating_desc: (a, b) => (b.rating || 0) - (a.rating || 0),
    },
    filters: [
      { name: 'type', label: 'Car Type', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.type)), apply: (r, v) => v.length === 0 || v.includes(r.type) },
      { name: 'capacity', label: 'Capacity', type: 'checkbox', options: (rs) => uniq(rs.map((r) => `${r.capacity} Seater`)), apply: (r, v) => v.length === 0 || v.includes(`${r.capacity} Seater`) },
      { name: 'priceRange', label: 'Price (per km)', type: 'range', key: 'price', apply: (r, [lo, hi]) => (lo == null || r.price >= lo) && (hi == null || r.price <= hi) },
    ],
  },

  villas: {
    label: 'Villas & Homestays',
    icon: Home,
    endpoint: '/services/villas',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'city', label: 'Destination', type: 'text', defaultValue: '', placeholder: 'Goa' },
      { name: 'check_in', label: 'Check-in', type: 'date', defaultValue: () => today(7) },
      { name: 'check_out', label: 'Check-out', type: 'date', defaultValue: () => today(9) },
      { name: 'guests', label: 'Guests', type: 'number', defaultValue: 2, min: 1, max: 12 },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'rating_desc', label: 'Top Rated' },
    ],
    sortFns: {
      price_asc: (a, b) => a.price - b.price,
      rating_desc: (a, b) => (b.rating || 0) - (a.rating || 0),
    },
    filters: [
      { name: 'type', label: 'Type', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.type)), apply: (r, v) => v.length === 0 || v.includes(r.type) },
      { name: 'amenities', label: 'Amenities', type: 'checkbox', options: (rs) => uniq(rs.flatMap((r) => r.amenities || [])), apply: (r, v) => v.length === 0 || v.every((a) => (r.amenities || []).includes(a)) },
      { name: 'priceRange', label: 'Price per night', type: 'range', key: 'price', apply: (r, [lo, hi]) => (lo == null || r.price >= lo) && (hi == null || r.price <= hi) },
    ],
  },

  packages: {
    label: 'Holiday Packages',
    icon: Package,
    endpoint: '/services/packages',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'destination', label: 'Destination', type: 'text', defaultValue: '', placeholder: 'Bali / Dubai / Kerala' },
      { name: 'start_date', label: 'Start Date', type: 'date', defaultValue: () => today(14) },
      { name: 'travellers', label: 'Travellers', type: 'number', defaultValue: 2, min: 1, max: 12 },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'rating_desc', label: 'Top Rated' },
    ],
    sortFns: {
      price_asc: (a, b) => a.price - b.price,
      rating_desc: (a, b) => (b.rating || 0) - (a.rating || 0),
    },
    filters: [
      { name: 'destination', label: 'Destination', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.destination)), apply: (r, v) => v.length === 0 || v.includes(r.destination) },
      { name: 'duration', label: 'Duration', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.duration)), apply: (r, v) => v.length === 0 || v.includes(r.duration) },
      { name: 'priceRange', label: 'Price', type: 'range', key: 'price', apply: (r, [lo, hi]) => (lo == null || r.price >= lo) && (hi == null || r.price <= hi) },
    ],
  },

  tours: {
    label: 'Tours & Activities',
    icon: MapPin,
    endpoint: '/services/tours',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'city', label: 'City', type: 'text', defaultValue: '', placeholder: 'Mumbai / Jaipur' },
      { name: 'tour_date', label: 'Date', type: 'date', defaultValue: () => today(3) },
      { name: 'travellers', label: 'Travellers', type: 'number', defaultValue: 2, min: 1, max: 12 },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'rating_desc', label: 'Top Rated' },
    ],
    sortFns: {
      price_asc: (a, b) => a.price - b.price,
      rating_desc: (a, b) => (b.rating || 0) - (a.rating || 0),
    },
    filters: [
      { name: 'location', label: 'Location', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.location)), apply: (r, v) => v.length === 0 || v.includes(r.location) },
      { name: 'duration', label: 'Duration', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.duration)), apply: (r, v) => v.length === 0 || v.includes(r.duration) },
      { name: 'priceRange', label: 'Price', type: 'range', key: 'price', apply: (r, [lo, hi]) => (lo == null || r.price >= lo) && (hi == null || r.price <= hi) },
    ],
  },

  visa: {
    label: 'Visa Services',
    icon: FileText,
    endpoint: '/services/visas',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'country', label: 'Country', type: 'text', defaultValue: '', placeholder: 'UAE / Singapore' },
      { name: 'travel_date', label: 'Travel Date', type: 'date', defaultValue: () => today(30) },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'processing_asc', label: 'Fastest Processing' },
    ],
    sortFns: {
      price_asc: (a, b) => a.price - b.price,
      processing_asc: (a, b) => (a.processingTime || '').localeCompare(b.processingTime || ''),
    },
    filters: [
      { name: 'country', label: 'Country', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.country)), apply: (r, v) => v.length === 0 || v.includes(r.country) },
      { name: 'priceRange', label: 'Price', type: 'range', key: 'price', apply: (r, [lo, hi]) => (lo == null || r.price >= lo) && (hi == null || r.price <= hi) },
    ],
  },

  cruise: {
    label: 'Cruises',
    icon: Ship,
    endpoint: '/services/cruises',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'destination', label: 'Destination', type: 'text', defaultValue: '', placeholder: 'Mumbai / Goa' },
      { name: 'sail_date', label: 'Sailing Date', type: 'date', defaultValue: () => today(21) },
      { name: 'guests', label: 'Guests', type: 'number', defaultValue: 2, min: 1, max: 10 },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'rating_desc', label: 'Top Rated' },
    ],
    sortFns: {
      price_asc: (a, b) => a.price - b.price,
      rating_desc: (a, b) => (b.rating || 0) - (a.rating || 0),
    },
    filters: [
      { name: 'duration', label: 'Duration', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.duration)), apply: (r, v) => v.length === 0 || v.includes(r.duration) },
      { name: 'priceRange', label: 'Price', type: 'range', key: 'price', apply: (r, [lo, hi]) => (lo == null || r.price >= lo) && (hi == null || r.price <= hi) },
    ],
  },

  forex: {
    label: 'Forex',
    icon: Banknote,
    endpoint: '/services/forex',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'currency', label: 'Currency', type: 'select', options: ['USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD'], defaultValue: 'USD' },
      { name: 'amount', label: 'Amount', type: 'number', defaultValue: 1000, min: 1 },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'rate_asc', label: 'Best Rate' },
    ],
    sortFns: {
      rate_asc: (a, b) => (a.rate || 0) - (b.rate || 0),
    },
    filters: [
      { name: 'type', label: 'Service Type', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.type)), apply: (r, v) => v.length === 0 || v.includes(r.type) },
      { name: 'provider', label: 'Provider', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.provider)), apply: (r, v) => v.length === 0 || v.includes(r.provider) },
    ],
  },

  insurance: {
    label: 'Travel Insurance',
    icon: ShieldCheck,
    endpoint: '/services/insurance',
    method: 'GET',
    headerGradient: 'from-red-600 to-red-800',
    fields: [
      { name: 'destination', label: 'Destination', type: 'text', defaultValue: '', placeholder: 'International / Domestic' },
      { name: 'travel_date', label: 'Travel Date', type: 'date', defaultValue: () => today(14) },
      { name: 'travellers', label: 'Travellers', type: 'number', defaultValue: 1, min: 1, max: 10 },
    ],
    buildPayload: () => null,
    extractResults: (data) => data || [],
    sortOptions: [
      { value: 'price_asc', label: 'Price (Low → High)' },
      { value: 'price_desc', label: 'Price (High → Low)' },
    ],
    sortFns: {
      price_asc: (a, b) => a.price - b.price,
      price_desc: (a, b) => b.price - a.price,
    },
    filters: [
      { name: 'duration', label: 'Duration', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.duration)), apply: (r, v) => v.length === 0 || v.includes(r.duration) },
      { name: 'provider', label: 'Provider', type: 'checkbox', options: (rs) => uniq(rs.map((r) => r.provider)), apply: (r, v) => v.length === 0 || v.includes(r.provider) },
      { name: 'priceRange', label: 'Price', type: 'range', key: 'price', apply: (r, [lo, hi]) => (lo == null || r.price >= lo) && (hi == null || r.price <= hi) },
    ],
  },
};
